import {IssueRelativeTime} from '@github-ui/ago'
import {announce} from '@github-ui/aria-live'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {SandboxView} from '@github-ui/sandbox-view'
import {sanitize, type SanitizedHTML} from '@github-ui/sanitize'
import {CodeIcon, EyeIcon} from '@primer/octicons-react'
import {SegmentedControl} from '@primer/react'
import {ScrollableRegion} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react'

import type {ScribeArtifactVersion} from '../types'
import styles from './ArtifactPanelContent.module.css'

export type ArtifactType = 'plan' | 'link' | 'embedded_html' | 'generic'

export type PanelArtifact = {
  id: string
  number?: number
  type: ArtifactType
  name: string
  body: string
  bodyHTML: string
  htmlUrl?: string
  trustedBodyHTML?: SanitizedHTML
  createdAt?: string
  updatedAt?: string
  hasEdits?: boolean
  updatedByActor?: {
    login: string
  }
  resourceType?: 'issue'
  resourceId?: string | number
  viewscreenUrl?: string
}

type ArtifactPanelBodyProps = {
  artifact: PanelArtifact
  selectedVersion?: ScribeArtifactVersion | null
}

/** Renders an artifact body as an HTML preview, Markdown, or plain text. */
export function ArtifactPanelBody({artifact, selectedVersion = null}: ArtifactPanelBodyProps) {
  const displayedBody = selectedVersion ? (selectedVersion.body ?? '') : artifact.body
  const displayedBodyHTML = selectedVersion ? (selectedVersion.body_html ?? '') : artifact.bodyHTML
  const trustedBodyHTML = selectedVersion ? (selectedVersion.body_html ?? undefined) : artifact.trustedBodyHTML
  const displayedAt = selectedVersion ? (selectedVersion.created_at ?? undefined) : artifact.updatedAt
  const displayedActor = selectedVersion ? (selectedVersion.actor ?? undefined) : artifact.updatedByActor
  const sanitizedHTML = useMemo(
    /* eslint-disable-next-line @github-ui/github-monorepo/no-custom-sanitize-config --
       Existing lax sanitizer usage; migrate to a named preset or VerifiedHTML when changed.
       This usage can probably migrate to the markdown preset. */
    () => trustedBodyHTML ?? (displayedBodyHTML ? sanitize(displayedBodyHTML) : ''),
    [displayedBodyHTML, trustedBodyHTML],
  )
  const viewscreenUrl = artifact.type === 'embedded_html' ? artifact.viewscreenUrl : undefined
  const selectedVersionNumber = selectedVersion?.version ?? null
  const htmlPreviewRef = useRef<HTMLDivElement>(null)
  const [htmlPreview, setHTMLPreview] = useState({
    artifactId: artifact.id,
    version: selectedVersionNumber,
    body: displayedBody,
    active: false,
  })
  const htmlSourceChanged =
    htmlPreview.artifactId !== artifact.id ||
    htmlPreview.version !== selectedVersionNumber ||
    htmlPreview.body !== displayedBody

  if (htmlSourceChanged) {
    setHTMLPreview({
      artifactId: artifact.id,
      version: selectedVersionNumber,
      body: displayedBody,
      active: false,
    })
  }
  const isHTMLPreviewActive = htmlPreview.active && !htmlSourceChanged

  useLayoutEffect(() => {
    if (isHTMLPreviewActive) {
      // The source scroll offset can hide the iframe beneath the sticky toolbar.
      htmlPreviewRef.current?.parentElement?.scrollTo(0, 0)
    }
  }, [isHTMLPreviewActive])

  return (
    <ScrollableRegion key={artifact.id} className={styles.Body} aria-label={artifact.name}>
      {displayedAt && (
        <div className={styles.Metadata}>
          <span className={styles.MetadataText}>
            <span>{selectedVersion ? 'Edited' : 'Updated'}</span> <IssueRelativeTime datetime={displayedAt} />
            {displayedActor && <span> by {displayedActor.login}</span>}
          </span>
        </div>
      )}
      {viewscreenUrl ? (
        <div ref={htmlPreviewRef} className={clsx(styles.HTMLPreview, isHTMLPreviewActive && styles.HTMLPreviewActive)}>
          <div className={styles.HTMLPreviewControls}>
            <SegmentedControl
              aria-label="HTML artifact view"
              onChange={index =>
                setHTMLPreview({
                  artifactId: artifact.id,
                  version: selectedVersionNumber,
                  body: displayedBody,
                  active: index === 1,
                })
              }
            >
              <SegmentedControl.IconButton aria-label="Source" icon={CodeIcon} selected={!isHTMLPreviewActive} />
              <SegmentedControl.IconButton
                aria-label="Preview"
                description="Previewing runs this code, which may send its contents to external sites."
                icon={EyeIcon}
                selected={isHTMLPreviewActive}
              />
            </SegmentedControl>
            <span className={styles.HTMLPreviewWarning}>
              Previewing runs this code, which may send its contents to external sites.
            </span>
          </div>
          {isHTMLPreviewActive ? (
            <>
              <div className={styles.HTMLPreviewAttribution}>
                Artifact HTML preview. If keyboard focus becomes trapped, use your browser&apos;s focus-navigation
                shortcut (usually F6) to leave the preview.
              </div>
              <div className={styles.HTMLPreviewFrame}>
                <SandboxView
                  key={selectedVersion?.version ?? artifact.id}
                  content={displayedBody}
                  restrictPermissions
                  title={`HTML preview: ${artifact.name}`}
                  viewscreenUrl={viewscreenUrl}
                />
              </div>
            </>
          ) : (
            <>
              <div className={styles.HTMLPreviewAttribution}>Artifact HTML source</div>
              <pre className={styles.HTMLSource}>{displayedBody}</pre>
            </>
          )}
        </div>
      ) : sanitizedHTML ? (
        <div className={styles.MarkdownContent}>
          <MarkdownViewer verifiedHTML={sanitizedHTML} />
        </div>
      ) : (
        <pre className={styles.PlainBody}>{displayedBody}</pre>
      )}
    </ScrollableRegion>
  )
}

export function useArtifactVersionSelection(artifact: PanelArtifact | null) {
  const [selection, setSelection] = useState<{artifactId: string; version: ScribeArtifactVersion} | null>(null)
  const artifactId = artifact?.id
  const artifactName = artifact?.name
  const selectedVersion = selection && selection.artifactId === artifactId ? selection.version : null
  const selectVersion = useCallback(
    (version: ScribeArtifactVersion) => {
      if (!artifactId) return

      setSelection({artifactId, version})
      announce(
        artifactName ? `Showing version ${version.version} of ${artifactName}.` : `Showing version ${version.version}.`,
      )
    },
    [artifactId, artifactName],
  )

  return {selectedVersion, selectVersion}
}
